import { createContext, useContext, useState, useEffect } from 'react';
import { auth, googleProvider, db } from '../config/firebase';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isEmailAllowed = async (email) => {
    try {
      const userDoc = await getDoc(doc(db, 'authorized_users', email));
      return userDoc.exists() && userDoc.data().active === true;
    } catch (err) {
      console.error('Error checking authorization:', err);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const isAllowed = await isEmailAllowed(currentUser.email);
        if (isAllowed) {
          setUser(currentUser);
        } else {
          await signOut(auth);
          setError('Solo usuarios autorizados pueden acceder al panel administrativo');
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const loginWithGoogle = async () => {
    try {
      setError(null);
      const result = await signInWithPopup(auth, googleProvider);
      
      const isAllowed = await isEmailAllowed(result.user.email);
      if (!isAllowed) {
        await signOut(auth);
        throw new Error('Solo usuarios autorizados pueden acceder al panel administrativo');
      }
      
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const value = {
    user,
    loading,
    error,
    loginWithGoogle,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
